import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Modal, Button, Alert } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { BudgetAllocationTable } from './BudgetAllocationTable'
import { AllocationBudgetRow, AllocationMethod, BudgetAllocationModalProps } from '../types'
import { DEFAULT_ALLOCATION_METHOD } from '../constants'
import { validateHistoryRangeLength } from '../functions/allocationFunctions'
import { applyAllocation } from '../utils/conversionUtils'

export const BudgetAllocationModal: React.FC<BudgetAllocationModalProps> = ({
  open,
  onClose,
  onSave,
  gridApi,
  columns,
  convertToAllocationRows,
  convertToBudgetRows
}) => {
  const { t } = useTranslation()

  // Row state
  const [rows, setRows] = useState<AllocationBudgetRow[]>([])

  // Save state
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Read rows from grid when modal opens
  useEffect(() => {
    if (open && gridApi) {
      // Stop editing to ensure current cell value is committed
      gridApi.stopEditing()

      // Collect rows with changes from grid
      const rowsToSave: any[] = []
      gridApi.forEachNode((node: any) => {
        if (node.data?.newBudgets || node.data?.newActuals) {
          rowsToSave.push(node.data)
        }
      })

      // Convert to allocation rows using custom converter or default
      const allocRows = convertToAllocationRows(rowsToSave, columns)

      // Set default allocation method for non-deleted rows
      setRows(
        allocRows.map(row => ({
          ...row,
          allocationMethod: row.isDeletion ? row.allocationMethod : row.allocationMethod ?? DEFAULT_ALLOCATION_METHOD
        }))
      )

      setSaveError(null) // Clear error when reopening modal
    }
  }, [open, gridApi, columns, convertToAllocationRows])

  // Validation and warnings
  const validation = useMemo(() => {
    const rowsWithWeighted = rows.filter(row => row.allocationMethod === AllocationMethod.WEIGHTED && !row.isDeletion)

    const errors: string[] = []
    const warnings: string[] = []

    if (rowsWithWeighted.length > 0) {
      // Check that all WEIGHTED rows have historyRange
      const rowsWithoutRange = rowsWithWeighted.filter(row => !row.allocationOptions?.historyRange)

      if (rowsWithoutRange.length > 0) {
        errors.push(
          t('budgetAllocationModal:errorMissingHistoryRange', {
            count: rowsWithoutRange.length
          })
        )
      }

      // Check that historyRange length matches targetPeriods length
      const rowsWithInvalidRange = rowsWithWeighted.filter(
        row =>
          row.allocationOptions?.historyRange &&
          !validateHistoryRangeLength(row.allocationOptions.historyRange, row.targetPeriods)
      )

      if (rowsWithInvalidRange.length > 0) {
        errors.push(
          t('budgetAllocationModal:errorInvalidHistoryRange', {
            count: rowsWithInvalidRange.length
          })
        )
      }

      // Info: how many rows use weighted history
      const validWeightedRows = rowsWithWeighted.length - rowsWithoutRange.length - rowsWithInvalidRange.length
      if (validWeightedRows > 0) {
        warnings.push(
          t('budgetAllocationModal:infoWeightedRows', {
            count: validWeightedRows
          })
        )
      }
    }

    return { errors, warnings, hasErrors: errors.length > 0 }
  }, [rows])

  // Update individual row allocation method (not for deleted rows)
  const handleMethodChange = useCallback((rowId: string, method: AllocationMethod) => {
    setRows(prev => prev.map(row => (row.id === rowId && !row.isDeletion ? { ...row, allocationMethod: method } : row)))
  }, [])

  // Update individual row options (not for deleted rows)
  const handleOptionsChange = useCallback((rowId: string, options: AllocationBudgetRow['allocationOptions']) => {
    setRows(prev =>
      prev.map(row => (row.id === rowId && !row.isDeletion ? { ...row, allocationOptions: options } : row))
    )
  }, [])

  // Save
  const handleSave = useCallback(async () => {
    setIsSaving(true)
    setSaveError(null) // Clear previous errors
    try {
      // Use applyAllocation with custom converter if provided
      const budgetData = applyAllocation(rows, convertToBudgetRows)
      await onSave(budgetData)
      onClose()
    } catch (error) {
      console.error('Budget save failed:', error)
      const errorMessage = error instanceof Error ? error.message : t('budgetAllocationModal:saveError')
      setSaveError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }, [rows, onSave, onClose, t, convertToBudgetRows])

  return (
    <Modal
      title={t('budgetAllocationModal:title')}
      open={open}
      onCancel={onClose}
      width="90vw"
      style={{ maxWidth: 1400 }}
      styles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          height: '60vh', // height relative to viewport -> shrinks with screen
          maxHeight: 900, // desired upper limit
          minHeight: 300, // must not shrink too much
          overflow: 'hidden' // prevent double scrollbars
        }
      }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('global:cancel', 'Peruuta')}
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} loading={isSaving} disabled={validation.hasErrors}>
          {t('global:save', 'Tallenna')}
        </Button>
      ]}
    >
      {/* Table */}
      <BudgetAllocationTable rows={rows} onMethodChange={handleMethodChange} onOptionsChange={handleOptionsChange} />

      {/* Validation errors */}
      {validation.errors.length > 0 && (
        <Alert
          style={{ marginTop: 16 }}
          type="error"
          icon={<ExclamationCircleOutlined />}
          message={t('budgetAllocationModal:validationErrorTitle')}
          description={validation.errors.map((error, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i}>{error}</div>
          ))}
        />
      )}

      {/* Warnings */}
      {validation.warnings.length > 0 && (
        <Alert
          style={{ marginTop: 16 }}
          type="info"
          message={validation.warnings.map((warning, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i}>{warning}</div>
          ))}
        />
      )}

      {/* Save error */}
      {saveError && (
        <Alert
          style={{ marginTop: 16 }}
          type="error"
          message={t('budgetAllocationModal:errorTitle')}
          description={saveError}
          closable
          onClose={() => setSaveError(null)}
        />
      )}
    </Modal>
  )
}
